import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { TextFieldProps } from "./TextField.types";
import styles from "./TextField.module.scss";
import { Typography } from "../index";
import { IconContext } from "react-icons";

const TextField = React.forwardRef<
  HTMLInputElement & HTMLTextAreaElement,
  TextFieldProps
>(
  (
    {
      label,
      required,
      helperText,
      hint,
      error,
      prefix,
      iconBefore,
      onIconBeforeClick,
      iconAfter,
      onIconAfterClick,
      link,
      onLinkClick,
      multiLine,
      initialValue,
      ...rest
    },
    ref
  ) => {
    const [inputState, setInputState] = useState<
      "normal" | "hover" | "focus" | "disabled" | "readOnly" | "error"
    >("normal");
    const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
    const [isDefaultValue, setIsDefaultValue] = useState(true);
    useImperativeHandle(ref, () => inputRef.current);

    useEffect(() => {
      if (rest.disabled) {
        setInputState("disabled");
      } else if (rest.readOnly) {
        setInputState("readOnly");
      } else if (error) {
        setInputState("error");
      } else {
        setInputState("normal");
      }
    }, [rest.disabled, rest.readOnly, error]);

    const handleInputFocus = (e: MouseEvent) => {
      if (e.type === "focus") {
        setInputState((prevState) => {
          if (prevState === "normal" || prevState === "hover") {
            return "focus";
          }
          return prevState;
        });
      } else if (e.type === "blur") {
        setInputState((prevState) => {
          if (prevState === "hover" || prevState === "focus") {
            return "normal";
          }
          return prevState;
        });
      } else if (e.type === "mouseenter") {
        setInputState((prevState) => {
          if (prevState === "normal") {
            return "hover";
          }
          return prevState;
        });
      } else if (e.type === "mouseleave") {
        setInputState((prevState) => {
          if (prevState === "hover") {
            return "normal";
          }
          return prevState;
        });
      }
    };

    useEffect(() => {
      setIsDefaultValue(inputRef.current.value === initialValue);
      ["mouseenter", "mouseleave", "focus", "blur"].forEach((event) => {
        inputRef.current.addEventListener(event, handleInputFocus);
      });

      return () => {
        ["mouseenter", "mouseleave", "focus", "blur"].forEach((event) => {
          const instance = inputRef.current;
          if (instance) {
            instance.removeEventListener(event, handleInputFocus);
          }
        });
      };
    }, []);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsDefaultValue(e.target.value === initialValue);
      rest.onChange && rest.onChange(e);
    };

    return (
      <div className={styles.TextField} data-testid="TextField">
        {(label || link || required || hint) && (
          <div className={styles.labelContainer}>
            <Typography
              token={100}
              weight={"normal"}
              variant={"label"}
              className={styles.label}
            >
              {label} {required && <span className={styles.required}>*</span>}
            </Typography>
            <div className={styles.rightSide}>
              {/*
                        Since the top right of the input can be a link or a normal hint,
                        if the user of the components implements both the hint will be ignored
                        and only the link will be displayed.
                    */}
              {link ? (
                <button className={styles.noStyleButton} onClick={onLinkClick}>
                  <Typography
                    token={100}
                    weight={"normal"}
                    variant={"p"}
                    className={styles.link}
                  >
                    {link}
                  </Typography>
                </button>
              ) : (
                hint && (
                  <Typography
                    token={100}
                    weight={"normal"}
                    variant={"p"}
                    className={styles.hint}
                  >
                    {hint}
                  </Typography>
                )
              )}
            </div>
          </div>
        )}
        <IconContext.Provider value={{ size: "16px" }}>
          <div className={styles.inputContainer} data-state={inputState}>
            {prefix && (
              <div className={styles.prefix}>
                <Typography variant={"p"} token={100} weight={"normal"}>
                  {prefix}
                </Typography>
              </div>
            )}
            {iconBefore && (
              <button
                type={"button"}
                className={[
                  styles.iconBefore,
                  !multiLine && styles.iconBeforeCenter,
                ].join(" ")}
                onClick={onIconBeforeClick}
              >
                {iconBefore}
              </button>
            )}
            {multiLine ? (
              <textarea
                ref={inputRef}
                {...rest}
                className={[styles.input, rest.className].join(" ")}
              />
            ) : (
              <input
                ref={inputRef}
                {...rest}
                onChange={(e) => onChange(e)}
                className={[
                  styles.input,
                  isDefaultValue ? styles.defaultValue : null,
                  rest.className,
                ].join(" ")}
              />
            )}
            {iconAfter && (
              <button
                type={"button"}
                className={[
                  styles.iconAfter,
                  !multiLine && styles.iconAfterCenter,
                ].join(" ")}
                onClick={onIconAfterClick}
              >
                {iconAfter}
              </button>
            )}
          </div>
        </IconContext.Provider>
        {helperText && (
          <div className={styles.helperText} data-state={inputState}>
            <Typography variant={"p"} token={100} weight={"normal"}>
              {helperText}
            </Typography>
          </div>
        )}
      </div>
    );
  }
);
TextField.displayName = "TextField";

export default TextField;
